/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable jsx-a11y/anchor-is-valid */
import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { APP_CONFIG } from "../../../config/config";
import { ENDPOINT } from "../../../services/api-endpoint";
import RecipeGradeMultiSelect from "../../../components/recipe-grade-multi-select/recipe-grade-multi-select";
import { useMaterialCatalog } from "../../../hooks/useMaterialCatalog";
import { parsePartMaterialIds } from "../../../utils/partMaterials";
import { addList, getList } from "../../../services/services";
import {
  PART_NUMBER_PLACEHOLDER,
  sanitizeStoredPartNumber,
  suggestNextPartNumber,
} from "../../../utils/partNumberSuggestion";
import "./create-order.scss";
import AppLoader from "../../../components/AppLoader";
import { usePageLoader } from "../../../services/usePageLoader";

type CatalogPart = {
  id: number;
  partname?: string | null;
  partnumber?: string | null;
};

type OrderPartLineDraft = {
  key: string;
  partid: number;
  partLabel: string;
  quantity: number;
  fromCatalog: boolean;
  workflowId: string;
  workflowLabel: string;
};

async function postOrderPartLine(
  payload: Record<string, unknown>,
): Promise<boolean> {
  try {
    const url = `${APP_CONFIG.API}${ENDPOINT.ORDER_PARTS_ADD}`;
    const res = await axios.post(url, payload);
    const d = res.data as unknown;
    if (d && typeof d === "object" && "id" in (d as object)) return true;
    const u = unwrapPost<{ id?: number }>(d);
    return Boolean(u?.id);
  } catch {
    return false;
  }
}

function unwrapPost<T>(body: unknown): T | null {
  if (
    body &&
    typeof body === "object" &&
    (body as { status?: string }).status === "success" &&
    "data" in body &&
    (body as { data: unknown }).data !== undefined
  ) {
    return (body as { data: T }).data;
  }
  return null;
}

async function postJson<T>(path: string, payload: unknown): Promise<T | null> {
  const url = `${APP_CONFIG.API}${path}`;
  const res = await axios.post(url, payload);
  return unwrapPost<T>(res.data);
}

const MesCreateOrder = () => {
  const navigate = useNavigate();
  const [validatedOrder, setValidatedOrder] = useState(false);
  const [validatedClient, setValidatedClient] = useState(false);
  const [formInputs, setForm] = useState<Record<string, string>>({});
  const [formClientInputs, setClientForm] = useState<Record<string, string>>({});
  const [formPartInputs, setPartForm] = useState<Record<string, string>>({
    partname: "",
    partnumber: "",
    materialcategory: "",
    imagrurl: "",
    description: "",
    material: "",
    drawingref: "",
    defaulttolerance: "",
    newpartqty: "1",
  });
  const [clientList, setClientList] = useState<any[]>([]);
  const [partsCatalog, setPartsCatalog] = useState<CatalogPart[]>([]);
  const [workflowsList, setWorkflowsList] = useState<any[]>([]);
  const [orderPartLines, setOrderPartLines] = useState<OrderPartLineDraft[]>([]);
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [inlineClientPanel, setInlineClientPanel] = useState(false);
  const [partEntryMode, setPartEntryMode] = useState<"catalog" | "new">(
    "catalog",
  );
  const [draftCatalogPartId, setDraftCatalogPartId] = useState("");
  const [draftPartQty, setDraftPartQty] = useState("1");
  const [draftPartWorkflowId, setDraftPartWorkflowId] = useState("");
  const { isLoading, runWithLoading } = usePageLoader();
  const { categories: materialCategories, grades: materialGrades } =
    useMaterialCatalog(formPartInputs.materialcategory || "");
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);

  useEffect(() => {
    setSelectedMaterialIds((prev) =>
      prev.filter((id) => materialGrades.some((g) => g.id === id)),
    );
  }, [materialGrades]);

  const resetNewPartForm = useCallback(() => {
    setPartForm({
      partname: "",
      partnumber: "",
      materialcategory: "",
      imagrurl: "",
      description: "",
      material: "",
      drawingref: "",
      defaulttolerance: "",
      newpartqty: "1",
    });
    setSelectedMaterialIds([]);
  }, []);

  const workflowLabel = useCallback(
    (id: string) => {
      if (!id) return "";
      const w = workflowsList.find((x: any) => String(x.id) === String(id));
      return w?.workflowname ? String(w.workflowname) : "";
    },
    [workflowsList],
  );

  const handleChanges = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((values) => ({ ...values, [name]: value }));
  };

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    setClientForm((values) => ({ ...values, [name]: value }));
  };

  const onCancel = () => navigate("/mes-order");

  const loadClients = useCallback(async () => {
    try {
      const list = await getList(ENDPOINT.CLIENT_LIST);
      setClientList(Array.isArray(list) ? list : []);
    } catch (e) {
      console.log(`Error loading clients: ${e}`);
    }
  }, []);

  const loadCatalogs = useCallback(async () => {
    try {
      const pReq = { pagesize: 500, pagenumber: 0, serachtext: "" };
      const pRaw = await getList(ENDPOINT.PARTS_LIST, pReq);
      const pItems = Array.isArray(pRaw) ? pRaw : (pRaw as any)?.data ?? [];
      setPartsCatalog(pItems);
    } catch (e) {
      console.log(`Error loading catalogs: ${e}`);
    }
  }, []);

  const loadWorkflows = useCallback(async () => {
    try {
      const req = { pagesize: 500, pagenumber: 0, serachtext: "" };
      const raw = await getList(ENDPOINT.WORKFLOWS_LIST, req);
      const items = Array.isArray(raw) ? raw : (raw as any)?.data ?? [];
      setWorkflowsList(items);
    } catch (e) {
      console.log(`Error loading workflows: ${e}`);
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadCatalogs();
    loadWorkflows();
  }, [loadClients, loadCatalogs, loadWorkflows]);

  const goNextWizardStep = () => {
    const form = document.getElementById(
      "manual-order-form",
    ) as HTMLFormElement | null;
    if (!form || !form.reportValidity()) {
      setValidatedOrder(true);
      return;
    }
    if (!formInputs.clientid) {
      setValidatedOrder(true);
      return;
    }
    setValidatedOrder(false);
    setWizardStep(2);
  };

  const handlePartFieldChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = event.target;
    if (name === "materialcategory") {
      setPartForm((values) => ({ ...values, materialcategory: value }));
      setSelectedMaterialIds([]);
      return;
    }
    setPartForm((values) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (wizardStep !== 2) return;
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidatedOrder(true);
      return;
    }
    if (!formInputs.clientid) {
      setValidatedOrder(true);
      return;
    }
    onSaveOrder();
  };

  async function onSaveOrder() {
    try {
      const request = {
        ordercode: formInputs.ordercode,
        clientid: Number(formInputs.clientid),
        startdate: formInputs.startdate,
        duedate: formInputs.duedate,
        active: true,
      };

      const created = await runWithLoading(async () =>
        postJson<{ id: number }>(ENDPOINT.ORDER_ADD, request),
      );

      if (!created?.id) {
        toast.error("Could not create order. Please try again.");
        return;
      }

      const orderId = created.id;

      for (const row of orderPartLines) {
        const ok = await postOrderPartLine({
          orderid: orderId,
          partid: row.partid,
          active: true,
          quantity: String(row.quantity),
          from_catalog: row.fromCatalog,
          workflowid: row.fromCatalog ? row.workflowId || null : null,
        });
        if (!ok) {
          toast.warn(
            `Order was created, but a line for "${row.partLabel}" could not be saved.`,
          );
        }
      }

      toast.success("Order created successfully.");
      navigate("/mes-order");
    } catch (e) {
      console.log(`Error creating order: ${e}`);
      toast.error("Something went wrong while creating the order.");
    }
  }

  async function saveClientAndSelect() {
    try {
      const request = {
        ...formClientInputs,
        active: true,
      };
      let status: string | unknown = "";
      await runWithLoading(async () => {
        status = await addList(ENDPOINT.CLIENT_ADD, request);
      });
      if (status !== "success") return;
      const listRaw = await getList(ENDPOINT.CLIENT_LIST);
      const list = Array.isArray(listRaw) ? listRaw : [];
      setClientList(list);
      const name = String(formClientInputs.clientname || "").trim();
      const email = String(formClientInputs.email || "").trim();
      const found = list.find(
        (c: any) =>
          String(c.clientname || "").trim() === name &&
          String(c.email || "").trim() === email,
      );
      if (found?.id != null) {
        setForm((v) => ({ ...v, clientid: String(found.id) }));
      }
      toast.success("Client added and selected.");
      setClientForm({});
      setValidatedClient(false);
      setInlineClientPanel(false);
    } catch (e) {
      console.log(`Error saving client: ${e}`);
    }
  }

  const handleInlineClientSubmit = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidatedClient(true);
      return;
    }
    saveClientAndSelect();
  };

  const addCatalogPartLine = () => {
    const pid = Number(draftCatalogPartId);
    if (!pid) {
      toast.info("Select a part from the catalog.");
      return;
    }
    if (orderPartLines.some((l) => l.partid === pid)) {
      toast.info("That part is already in the order list.");
      return;
    }
    const qty = Math.max(1, Math.floor(Number(draftPartQty) || 1));
    const p = partsCatalog.find((x) => x.id === pid);
    const base = p?.partname?.trim() || `Part #${pid}`;
    const label = p?.partnumber?.trim() ? `${base} (${p.partnumber})` : base;
    const wfId = draftPartWorkflowId;
    const line: OrderPartLineDraft = {
      key: `${pid}-${Date.now()}`,
      partid: pid,
      partLabel: label,
      quantity: qty,
      fromCatalog: true,
      workflowId: wfId,
      workflowLabel: wfId ? workflowLabel(wfId) : "Standard",
    };
    setOrderPartLines((rows) => [...rows, line]);
    setDraftCatalogPartId("");
    setDraftPartQty("1");
    setDraftPartWorkflowId("");
    toast.success("Part added to order.");
  };

  const handleInlineNewPartCreate = async () => {
    const name = String(formPartInputs.partname || "").trim();
    if (!name) {
      toast.info("Enter a part name.");
      return;
    }
    const qty = Math.max(1, Math.floor(Number(formPartInputs.newpartqty) || 1));
    let partNum = sanitizeStoredPartNumber(String(formPartInputs.partnumber || ""));
    if (!partNum) {
      partNum = suggestNextPartNumber(partsCatalog);
    }
    try {
      const request = {
        partname: name,
        partnumber: partNum || null,
        materialcategory:
          String(formPartInputs.materialcategory || "").trim() || null,
        materialIds: selectedMaterialIds,
        drawingref: String(formPartInputs.drawingref || "").trim() || null,
        defaulttolerance:
          String(formPartInputs.defaulttolerance || "").trim() || null,
        imagrurl: String(formPartInputs.imagrurl || "").trim() || null,
        description: String(formPartInputs.description || "").trim() || null,
        active: true,
      };
      const created = await runWithLoading(async () =>
        postJson<{ id: number }>(ENDPOINT.PARTS_ADD, request),
      );
      if (!created?.id) {
        toast.error("Could not create part.");
        return;
      }
      await loadCatalogs();
      const lineLabel =
        partNum && partNum !== name ? `${name} (${partNum})` : name;
      const line: OrderPartLineDraft = {
        key: `${created.id}-${Date.now()}`,
        partid: created.id,
        partLabel: lineLabel,
        quantity: qty,
        fromCatalog: false,
        workflowId: "",
        workflowLabel: "",
      };
      setOrderPartLines((rows) => [...rows, line]);
      resetNewPartForm();
      setPartEntryMode("catalog");
      toast.success("Part created and added to order.");
    } catch (e) {
      console.log(`Error saving part: ${e}`);
      toast.error("Something went wrong while adding the part.");
    }
  };

  const removePartLine = (key: string) => {
    setOrderPartLines((rows) => rows.filter((r) => r.key !== key));
  };

  const catalogPartsAvailable = useMemo(() => {
    const used = new Set(orderPartLines.map((l) => l.partid));
    return partsCatalog.filter((p) => !used.has(p.id));
  }, [partsCatalog, orderPartLines]);

  return (
    <div className="manual-order-page mes-partner-portal">
      {isLoading && <AppLoader text="Working..." />}
      <Modal
        show
        onHide={onCancel}
        centered
        backdrop="static"
        keyboard={false}
        className="manual-order-v2"
        backdropClassName="manual-order-modal-backdrop mes-v2-order-backdrop"
        dialogClassName={`manual-order-modal-dialog mes-v2-order-dialog${
          wizardStep === 2 ? " manual-order-modal-dialog--wide" : ""
        }`}
        contentClassName="manual-order-modal-content mes-v2-order-content"
      >
            <Modal.Header closeButton={false} className="manual-order-modal__header mes-v2-order-header manual-order-modal__header--wizard">
              <div className="manual-order-modal__header-row">
                <div className="manual-order-modal__header-inner">
                  <span
                    className="manual-order-modal__icon-wrap manual-order-modal__icon-wrap--wizard"
                    aria-hidden
                  >
                    <i className="fa fa-upload" />
                  </span>
                  <div className="manual-order-modal__titles">
                    <Modal.Title as="h5" className="manual-order-modal__title">
                      Create Manual Order
                    </Modal.Title>
                    <p className="manual-order-modal__subtitle">
                      {wizardStep === 1
                        ? "Step 1: Enter order details and select the client."
                        : "Step 2: Add parts for this order. Create new parts or select from catalog. Workflow templates can be assigned when selecting from catalog."}
                    </p>
                  </div>
                </div>
                <div className="manual-order-modal__header-aside">
                  <span className="manual-order-modal__step-pill">
                    Step {wizardStep} of 2
                  </span>
                  <button
                    type="button"
                    className="manual-order-modal__close"
                    onClick={onCancel}
                    aria-label="Close"
                  >
                    <i className="fa fa-times" aria-hidden />
                  </button>
                </div>
              </div>
            </Modal.Header>
            <Modal.Body className="manual-order-modal__body">
              {wizardStep === 1 && (
                <div className="manual-order-modal__step1-fields mb-3">
                  <div className="manual-order-modal__label-row">
                    <label
                      className="manual-order-modal__label-primary mb-0"
                      htmlFor="manual-order-clientid"
                    >
                      Client
                    </label>
                    <button
                      type="button"
                      className="manual-order-modal__link-add"
                      onClick={() => {
                        setInlineClientPanel((o) => {
                          if (o) {
                            setValidatedClient(false);
                            setClientForm({});
                          }
                          return !o;
                        });
                      }}
                    >
                      {inlineClientPanel ? "Close" : "+ Add new client"}
                    </button>
                  </div>
                  <select
                    id="manual-order-clientid"
                    form="manual-order-form"
                    name="clientid"
                    required
                    className={`custom-select manual-order-modal__select${validatedOrder && !formInputs.clientid ? " is-invalid" : ""}`}
                    value={formInputs.clientid || ""}
                    onChange={(e) =>
                      setForm((values) => ({
                        ...values,
                        clientid: e.target.value,
                      }))
                    }
                    aria-label="Select client"
                  >
                    <option value="">Select client</option>
                    {clientList?.map((list: any) => {
                      const primary =
                        list.companyorgname?.trim() || list.clientname || "";
                      const contact = list.clientname?.trim() || "";
                      const label =
                        primary && contact && primary !== contact
                          ? `${primary} — ${contact}`
                          : primary || `Client #${list.id}`;
                      return (
                        <option key={list.id} value={list.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  {validatedOrder && !formInputs.clientid && (
                    <div className="invalid-feedback d-block">
                      Please select a client.
                    </div>
                  )}
                  {inlineClientPanel && (
                    <Form
                      id="inline-client-form"
                      noValidate
                      validated={validatedClient}
                      onSubmit={handleInlineClientSubmit}
                      className="manual-order-modal__new-entity manual-order-modal__new-entity--client mt-3"
                    >
                      <div className="manual-order-modal__new-entity-banner">
                        NEW CLIENT — WILL BE ADDED TO DIRECTORY
                      </div>
                      <Row className="mb-2">
                        <Form.Group as={Col} md={6}>
                          <Form.Label>Name *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="clientname"
                            value={formClientInputs.clientname || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group as={Col} md={6}>
                          <Form.Label>Email *</Form.Label>
                          <Form.Control
                            required
                            type="email"
                            name="email"
                            value={formClientInputs.email || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Col} md={6}>
                          <Form.Label>Phone *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="contactphone"
                            value={formClientInputs.contactphone || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group as={Col} md={6}>
                          <Form.Label>Company *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="companyorgname"
                            value={formClientInputs.companyorgname || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Col} md={6}>
                          <Form.Label>Address Line 1 *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="addresslineone"
                            value={formClientInputs.addresslineone || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group as={Col} md={6}>
                          <Form.Label>Address Line 2</Form.Label>
                          <Form.Control
                            type="text"
                            name="addresslinetwo"
                            value={formClientInputs.addresslinetwo || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Col} md={4}>
                          <Form.Label>City *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="city"
                            value={formClientInputs.city || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group as={Col} md={4}>
                          <Form.Label>State *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="state"
                            value={formClientInputs.state || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group as={Col} md={4}>
                          <Form.Label>Country *</Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="country"
                            value={formClientInputs.country || ""}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Row>
                      <button
                        type="submit"
                        className="manual-order-modal__btn-entity-wide"
                      >
                        + Create Client & Use for Order
                      </button>
                      <p className="manual-order-modal__new-entity-hint mb-0 mt-2">
                        Client will be saved to the directory and selected for
                        this order.
                      </p>
                    </Form>
                  )}
                </div>
              )}
              <Form
                noValidate
                validated={validatedOrder}
                onSubmit={handleSubmit}
                id="manual-order-form"
              >
                <div
                  className={
                    wizardStep === 1
                      ? "manual-order-modal__wizard-step"
                      : "manual-order-modal__wizard-step d-none"
                  }
                >
                  <Form.Group className="mb-3" controlId="ordercode">
                    <Form.Label className="manual-order-modal__label-primary">
                      Client Order Code
                    </Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="ordercode"
                      placeholder="e.g. PO-2024-1234"
                      value={formInputs.ordercode || ""}
                      onChange={handleChanges}
                    />
                    <Form.Control.Feedback type="invalid">
                      Client order code is required.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Row className="mb-0">
                    <Form.Group as={Col} md={6} controlId="startdate">
                      <Form.Label className="manual-order-modal__label-primary">
                        Start Date
                      </Form.Label>
                      <Form.Control
                        required
                        type="date"
                        name="startdate"
                        value={formInputs.startdate || ""}
                        onChange={handleChanges}
                      />
                      <Form.Control.Feedback type="invalid">
                        Start date is required.
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md={6} controlId="duedate">
                      <Form.Label className="manual-order-modal__label-primary">
                        Due Date
                      </Form.Label>
                      <Form.Control
                        required
                        type="date"
                        name="duedate"
                        value={formInputs.duedate || ""}
                        onChange={handleChanges}
                      />
                      <Form.Control.Feedback type="invalid">
                        Due date is required.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </div>

                <div
                  className={
                    wizardStep === 2
                      ? "manual-order-modal__wizard-step"
                      : "manual-order-modal__wizard-step d-none"
                  }
                >
                  <div className="manual-order-modal__add-card manual-order-modal__add-card--parts">
                    <div className="manual-order-modal__add-card-head">
                      <span className="manual-order-modal__add-card-title">
                        Add Order Parts
                      </span>
                      {partEntryMode === "catalog" ? (
                        <button
                          type="button"
                          className="manual-order-modal__link-add"
                          onClick={() => {
                            setPartEntryMode("new");
                            resetNewPartForm();
                            const pn = suggestNextPartNumber(partsCatalog);
                            setPartForm((f) => ({ ...f, partnumber: pn }));
                          }}
                        >
                          + Create New Part
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="manual-order-modal__link-add"
                          onClick={() => {
                            setPartEntryMode("catalog");
                            resetNewPartForm();
                          }}
                        >
                          + Select Existing
                        </button>
                      )}
                    </div>

                    {partEntryMode === "catalog" ? (
                      <>
                        <Form.Group className="mb-3" controlId="catalogpart">
                          <Form.Label className="manual-order-modal__label-primary">
                            Part from Catalog
                          </Form.Label>
                          <Form.Select
                            className="custom-select"
                            value={draftCatalogPartId}
                            onChange={(e) =>
                              setDraftCatalogPartId(e.target.value)
                            }
                            aria-label="Select existing part"
                          >
                            <option value="">Select existing part</option>
                            {catalogPartsAvailable.map((p) => {
                              const label = p.partname?.trim() || `Part #${p.id}`;
                              const num = p.partnumber?.trim();
                              return (
                                <option key={p.id} value={p.id}>
                                  {num ? `${label} · ${num}` : label}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </Form.Group>
                        <Row className="mb-3">
                          <Form.Group as={Col} md={6}>
                            <Form.Label className="manual-order-modal__label-primary">
                              Quantity
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              value={draftPartQty}
                              onChange={(e) => setDraftPartQty(e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group as={Col} md={6}>
                            <Form.Label className="manual-order-modal__label-primary">
                              Workflow Template
                            </Form.Label>
                            <Form.Select
                              className="custom-select"
                              value={draftPartWorkflowId}
                              onChange={(e) =>
                                setDraftPartWorkflowId(e.target.value)
                              }
                            >
                              <option value="">Optional</option>
                              {workflowsList.map((w: any) => (
                                <option key={w.id} value={w.id}>
                                  {w.workflowname}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Row>
                        <button
                          type="button"
                          className="manual-order-modal__btn-add-line"
                          onClick={addCatalogPartLine}
                        >
                          + Add This Part
                        </button>
                      </>
                    ) : (
                      <div className="manual-order-modal__new-entity manual-order-modal__new-entity--part">
                        <div className="manual-order-modal__new-entity-banner">
                          NEW PART — WILL BE ADDED TO PARTS CATALOG
                        </div>
                        <Row className="mb-2">
                          <Form.Group as={Col} md={6}>
                            <Form.Label>Part Name *</Form.Label>
                            <Form.Control
                              type="text"
                              name="partname"
                              placeholder="e.g. Crankshaft Housing"
                              value={formPartInputs.partname || ""}
                              onChange={handlePartFieldChange}
                            />
                          </Form.Group>
                          <Form.Group as={Col} md={6}>
                            <Form.Label>
                              Part Number (auto-generated suggestion)
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="partnumber"
                              placeholder={PART_NUMBER_PLACEHOLDER}
                              value={formPartInputs.partnumber || ""}
                              onChange={handlePartFieldChange}
                              onFocus={() => {
                                setPartForm((prev) =>
                                  sanitizeStoredPartNumber(prev.partnumber)
                                    ? prev
                                    : {
                                        ...prev,
                                        partnumber: suggestNextPartNumber(
                                          partsCatalog,
                                        ),
                                      },
                                );
                              }}
                            />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Col} md={6}>
                            <Form.Label>Material Category</Form.Label>
                            <Form.Select
                              className="custom-select"
                              name="materialcategory"
                              value={formPartInputs.materialcategory || ""}
                              onChange={handlePartFieldChange}
                            >
                              <option value="">Select category…</option>
                              {materialCategories.map((c) => (
                                <option key={c.id} value={String(c.id)}>
                                  {c.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                          <Form.Group as={Col} md={6}>
                            <Form.Label>Material</Form.Label>
                            <RecipeGradeMultiSelect
                              disabled={!formPartInputs.materialcategory}
                              grades={materialGrades}
                              value={selectedMaterialIds}
                              onChange={setSelectedMaterialIds}
                              placeholder={
                                formPartInputs.materialcategory
                                  ? "Search and select materials…"
                                  : "Select a category first"
                              }
                            />
                          </Form.Group>
                        </Row>
                        <Form.Group className="mb-2">
                          <Form.Label>Drawing Reference</Form.Label>
                          <Form.Control
                            type="text"
                            name="drawingref"
                            placeholder="e.g. DWG-2024-001"
                            value={formPartInputs.drawingref || ""}
                            onChange={handlePartFieldChange}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Default Tolerance</Form.Label>
                          <Form.Control
                            type="text"
                            name="defaulttolerance"
                            placeholder="e.g. ±0.01mm"
                            value={formPartInputs.defaulttolerance || ""}
                            onChange={handlePartFieldChange}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Image URL</Form.Label>
                          <Form.Control
                            type="text"
                            name="imagrurl"
                            value={formPartInputs.imagrurl || ""}
                            onChange={handlePartFieldChange}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Description (optional)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={formPartInputs.description || ""}
                            onChange={handlePartFieldChange}
                          />
                        </Form.Group>
                        <Row className="mb-3">
                          <Form.Group as={Col} md={6}>
                            <Form.Label>Quantity *</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              name="newpartqty"
                              value={formPartInputs.newpartqty || "1"}
                              onChange={handlePartFieldChange}
                            />
                          </Form.Group>
                        </Row>
                        <button
                          type="button"
                          className="manual-order-modal__btn-entity-wide"
                          onClick={handleInlineNewPartCreate}
                        >
                          + Create Part & Add to Order
                        </button>
                        <p className="manual-order-modal__new-entity-hint mb-0 mt-2">
                          Part will be saved to Parts Catalog automatically.
                        </p>
                      </div>
                    )}
                  </div>

                  {orderPartLines.length > 0 && (
                    <div className="manual-order-modal__order-lines mt-3">
                      <div className="manual-order-modal__order-lines-title">
                        Order Parts ({orderPartLines.length})
                      </div>
                      <ul className="manual-order-modal__order-lines-list list-unstyled mb-0">
                        {orderPartLines.map((row) => (
                          <li
                            key={row.key}
                            className="manual-order-modal__order-line"
                          >
                            <div>
                              <div className="manual-order-modal__order-line-title">
                                {row.partLabel} × {row.quantity}
                              </div>
                              <div className="manual-order-modal__order-line-sub">
                                {row.partLabel}
                                {row.fromCatalog
                                  ? ` — ${row.workflowLabel || "Standard"}`
                                  : ""}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="manual-order-modal__order-line-remove"
                              onClick={() => removePartLine(row.key)}
                              aria-label={`Remove ${row.partLabel}`}
                            >
                              <i className="fa fa-trash-o" aria-hidden />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Form>
            </Modal.Body>
            <Modal.Footer className="manual-order-modal__footer manual-order-modal__footer--wizard">
              <div className="manual-order-modal__footer-stack">
                <div className="manual-order-modal__footer-meta">
                  <span className="manual-order-modal__badge manual-order-modal__badge--manual">
                    Manual
                  </span>
                  <span className="manual-order-modal__footer-hint">
                    Source will be set automatically
                  </span>
                </div>
                <div className="manual-order-modal__footer-actions">
                  {wizardStep === 1 ? (
                    <>
                      <button
                        type="button"
                        className="manual-order-modal__btn-cancel manual-order-modal__btn-cancel--outline"
                        onClick={onCancel}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="manual-order-modal__btn-next"
                        onClick={goNextWizardStep}
                      >
                        Next Step
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="manual-order-modal__btn-cancel manual-order-modal__btn-cancel--outline"
                        onClick={onCancel}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="manual-order-modal__btn-cancel manual-order-modal__btn-cancel--outline"
                        onClick={() => {
                          setWizardStep(1);
                          setValidatedOrder(false);
                        }}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        form="manual-order-form"
                        className="manual-order-modal__btn-create manual-order-modal__btn-create--wizard"
                      >
                        Create Order
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Modal.Footer>
          </Modal>
    </div>
  );
};

export default MesCreateOrder;
